import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Notepad, SealCheck } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { useFormat } from '@/i18n'
import { Banner, DataTable, type Column } from '@/ui'
import {
  reconcileBlockers,
  useReconcileSession,
  useRolls,
  useSession,
  useSessionReport,
  type SessionLot,
} from '../api/gate'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { ExceptionTable } from '../components/ExceptionTable'
import { ConsolePage, DetailColumns, QueryState } from '../components/Layout'
import { RollActions } from '../components/RollActions'
import {
  Check,
  Code,
  Facts,
  Kg,
  RefLink,
  Section,
  Status,
  Stream,
  UserName,
  When,
} from '../components/bits'
import { useScope } from '../api/appConfig'
import { lotHoldLabel } from '../labels'

/** One collection day: reconciliation, day report, lots, exceptions, rolls (F4.11-c/e, F4-B1.a-d, F4-B2.c-c). */
export function SessionDetailPage() {
  const { id } = useParams()
  const q = useSession(id)
  const s = q.data
  return (
    <ConsolePage
      back={{ to: '/c/sessions', label: 'Collection days' }}
      eyebrow={s ? `${s.captureMode === 'PAPER' ? 'Paper day' : 'App day'}` : 'Collection day'}
      title={s ? <span className="font-mono">{s.code}</span> : 'Collection day'}
      actions={s && <SessionActions id={s.id} />}
    >
      <QueryState query={q} label="the day">
        {s && <SessionBody />}
      </QueryState>
    </ConsolePage>
  )
}

function SessionActions({ id }: { id: string }) {
  const { v1 } = useScope()
  if (v1) return null
  return <FullSpecActions id={id} />
}

/** Full spec only: paper review and the central reconcile (both off in V1). */
function FullSpecActions({ id }: { id: string }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const s = useSession(id).data!
  const reconcile = useReconcileSession()
  const [open, setOpen] = useState(false)
  const blockers = reconcileBlockers(s)
  const own =
    user && s.operatorId === user.id ? 'You ran this day: someone else reconciles it.' : null
  return (
    <>
      {s.captureMode === 'PAPER' && (
        <ActionButton
          perm="units:review-paper"
          hide
          variant="secondary"
          leadingIcon={<Notepad />}
          onClick={() => navigate(`/c/paper-review?sessionId=${s.id}`)}
        >
          Paper review
        </ActionButton>
      )}
      {s.status === 'CLOSED' && (
        <ActionButton
          perm="collection-sessions:reconcile"
          leadingIcon={<SealCheck />}
          blockedReason={own}
          onClick={() => setOpen(true)}
        >
          Reconcile
        </ActionButton>
      )}
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Reconcile ${s.code}`}
        description="Reconciling closes the day for good: every command arrived, the counts balance (or the difference was settled), no tag conflict is open."
        confirmLabel="Reconcile"
        loading={reconcile.isPending}
        onConfirm={() => reconcile.mutateAsync(s.id).then(() => setOpen(false))}
      >
        {blockers.length > 0 ? (
          <Banner tone="warn" title="The server will refuse this until:">
            <ul className="list-disc pl-4">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </Banner>
        ) : (
          <Banner tone="success">Everything balances.</Banner>
        )}
      </ConfirmDialog>
    </>
  )
}

function SessionBody() {
  const { id } = useParams()
  const s = useSession(id).data!
  const { v1 } = useScope()
  const report = useSessionReport(id)
  const r = s.reconciliation
  const v = r?.verdict
  const lotCols: Column<SessionLot>[] = [
    { id: 'code', header: 'Lot', cell: (l) => <RefLink to={`/c/lots/${l.id}`}>{l.code}</RefLink> },
    { id: 'stream', header: 'Stream', cell: (l) => <Stream stream={l.stream} />, hideBelow: 'sm' },
    { id: 'bags', header: 'Bags', align: 'right', mono: true, cell: (l) => l.bagCount },
    { id: 'net', header: 'Net', align: 'right', cell: (l) => <Kg grams={l.netG} /> },
    {
      id: 'farmer',
      header: 'Farmer',
      cell: (l) => (l.farmerId ? <UserName id={l.farmerId} /> : '—'),
      hideBelow: 'md',
    },
    {
      id: 'hold',
      header: 'Hold',
      cell: (l) =>
        l.hold === 'NONE' ? (
          <span className="text-muted">—</span>
        ) : (
          <Status status={l.hold} label={l.holdReasons.map(lotHoldLabel).join(', ') || l.hold} />
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (l) => (
        <Status status={l.status} label={l.voidReason ? `Void · ${l.voidReason}` : undefined} />
      ),
    },
  ]
  return (
    <DetailColumns
      main={
        <>
          {v1 ? (
            <Section title="Records from the phone" eyebrow="Sync">
              {!r ? (
                <p className="text-[14px] text-muted">Shown once the day is closed on the phone.</p>
              ) : (
                <Facts
                  columns={2}
                  items={[
                    {
                      label: 'Records arrived',
                      value: (
                        <span className="inline-flex items-center gap-2 font-mono">
                          {r.commandsArrived} / {r.commandsExpected ?? '?'}{' '}
                          <Check ok={v?.allCommandsArrived} />
                        </span>
                      ),
                    },
                    {
                      label: 'Payments',
                      value: v?.allCommandsArrived
                        ? 'Go as the lots arrive'
                        : 'Wait for the missing records: keep the phone on network',
                    },
                  ]}
                />
              )}
            </Section>
          ) : (
            <FullSpecCount />
          )}

          <Section title="Day report">
            <QueryState query={report} label="the report">
              {report.data && (
                <Facts
                  columns={4}
                  items={[
                    {
                      label: 'Lots',
                      value: `${report.data.lots} (${report.data.lotsMain} good · ${report.data.lotsConv} ordinary)`,
                    },
                    { label: 'Deliveries', value: report.data.deliveries, mono: true },
                    { label: 'Net kg', value: <Kg grams={report.data.netG} /> },
                    { label: 'Bags', value: report.data.bags, mono: true },
                    {
                      label: 'Ordinary share',
                      value: `${report.data.ordinarySharePct} %`,
                      mono: true,
                    },
                    { label: 'Typed tags', value: `${report.data.typedTagSharePct} %`, mono: true },
                    ...(v1
                      ? []
                      : [
                          {
                            label: 'Photo-only IDs',
                            value: report.data.photoOnlyIdentifications,
                            mono: true,
                          },
                        ]),
                    { label: 'Storage holds', value: report.data.storageHolds, mono: true },
                    { label: 'Diverted to dry', value: report.data.diverts, mono: true },
                    { label: 'Walked away', value: report.data.walkAways, mono: true },
                    {
                      label: 'Deleted (wrong farmer / twice)',
                      value: report.data.deletedByMistake,
                      mono: true,
                    },
                    ...(v1
                      ? []
                      : [
                          {
                            label: 'Disputes',
                            value: `${report.data.disputes} (${report.data.disputesOpen} open) · ${report.data.disputeSharePct} %`,
                            mono: true,
                          },
                          {
                            label: 'Brought by others',
                            value: report.data.broughtByOthers,
                            mono: true,
                          },
                        ]),
                  ]}
                />
              )}
              {report.data && report.data.payoutStatuses.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {report.data.payoutStatuses.map((p) => (
                    <Link key={p.status} to={`/c/payouts?sessionId=${s.id}&status=${p.status}`}>
                      <Status
                        status={p.status}
                        label={`${p.n} · ${p.status.replace(/_/g, ' ').toLowerCase()}`}
                      />
                    </Link>
                  ))}
                </div>
              )}
            </QueryState>
          </Section>

          <DataTable<SessionLot>
            caption="Lots of the day"
            columns={lotCols}
            rows={s.lots}
            getRowId={(l) => l.id}
            searchText={(l) => l.code}
            searchPlaceholder="Find a lot"
            filters={
              <span className="font-mono text-[11px] font-bold uppercase tracking-label text-muted">
                Lots · {s.lots.length}
              </span>
            }
          />
        </>
      }
      side={
        <>
          <Section title="Day">
            <Facts
              columns={2}
              items={[
                { label: 'Status', value: <Status status={s.status} /> },
                {
                  label: 'Mode',
                  value: <Status status={s.captureMode === 'PAPER' ? 'PAPER' : 'APP'} />,
                },
                { label: 'Operator', value: <UserName id={s.operatorId} /> },
                { label: 'Opened', value: <When at={s.openedAt} /> },
                { label: 'Closed', value: <When at={s.closedAt} /> },
                ...(v1 ? [] : [{ label: 'Reconciled', value: <When at={s.reconciledAt} /> }]),
                ...(!v1 && s.testWeight
                  ? [
                      {
                        label: 'Test weight',
                        value: `${s.testWeight.readKg ?? '—'} / ${s.testWeight.expectedKg ?? '—'} kg`,
                        mono: true,
                      },
                    ]
                  : []),
              ]}
            />
          </Section>
          <Section
            title="Exceptions"
            eyebrow={`${s.exceptions.filter((e) => e.status === 'OPEN').length} open`}
            bodyClassName="p-0"
          >
            <ExceptionTable rows={s.exceptions} compact />
          </Section>
          {!v1 && <OperatorRolls />}
          {s.deviceId && (
            <Section title="Phone">
              <Code>{s.deviceId}</Code>
            </Section>
          )}
        </>
      }
    />
  )
}

/** Full spec only: tags, seals and bags counted at close against what the phone used (off in V1). */
function FullSpecCount() {
  const { id } = useParams()
  const s = useSession(id).data!
  const r = s.reconciliation
  const v = r?.verdict
  return (
    <Section title="The day's count" eyebrow="Reconciliation">
      {!r ? (
        <p className="text-[14px] text-muted">
          The count appears when the day is closed on the phone.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-[14px]">
              <caption className="sr-only">Tags and seals</caption>
              <thead className="font-mono text-[10.5px] uppercase tracking-label text-muted">
                <tr>
                  <th scope="col" className="py-1 text-left">
                    Kind
                  </th>
                  <th scope="col" className="text-right">
                    At open
                  </th>
                  <th scope="col" className="text-right">
                    + Rolls
                  </th>
                  <th scope="col" className="text-right">
                    − Used
                  </th>
                  <th scope="col" className="text-right">
                    − Voided
                  </th>
                  <th scope="col" className="text-right">
                    = Expected left
                  </th>
                  <th scope="col" className="text-right">
                    Counted
                  </th>
                  <th scope="col" className="text-right">
                    OK
                  </th>
                </tr>
              </thead>
              <tbody className="font-mono tabular">
                <tr className="border-t border-rule/70">
                  <th scope="row" className="py-1.5 text-left font-sans font-normal">
                    Bag tags
                  </th>
                  <td className="text-right">{r.tagsAtOpen}</td>
                  <td className="text-right">{r.tagsConfirmed}</td>
                  <td className="text-right">{r.tagsBound}</td>
                  <td className="text-right">{r.tagsVoided}</td>
                  <td className="text-right">{v?.tagsExpectedLeft ?? '—'}</td>
                  <td className="text-right">{r.tagsCounted ?? '—'}</td>
                  <td className="text-right">
                    <Check ok={v?.tagsBalanced} />
                  </td>
                </tr>
                <tr className="border-t border-rule/70">
                  <th scope="row" className="py-1.5 text-left font-sans font-normal">
                    Sample seals
                  </th>
                  <td className="text-right">{r.sealsAtOpen}</td>
                  <td className="text-right">{r.sealsConfirmed}</td>
                  <td className="text-right">{r.sealsBound}</td>
                  <td className="text-right">{r.sealsVoided}</td>
                  <td className="text-right">{v?.sealsExpectedLeft ?? '—'}</td>
                  <td className="text-right">{r.sealsCounted ?? '—'}</td>
                  <td className="text-right">
                    <Check ok={v?.sealsBalanced} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Facts
            columns={3}
            items={[
              {
                label: 'Bags counted / tagged',
                value: (
                  <span className="inline-flex items-center gap-2 font-mono">
                    {r.bagsCounted} / {r.bagsTagged} <Check ok={v?.bagsBalanced} />
                  </span>
                ),
              },
              {
                label: 'Commands arrived',
                value: (
                  <span className="inline-flex items-center gap-2 font-mono">
                    {r.commandsArrived} / {r.commandsExpected ?? '?'}{' '}
                    <Check ok={v?.allCommandsArrived} />
                  </span>
                ),
              },
              {
                label: 'Verdict',
                value: v?.balanced ? (
                  <Status status="OK" label="Balanced" />
                ) : (
                  <Status status="HOLD" label="Does not balance" />
                ),
              },
            ]}
          />
          {s.status === 'CLOSED' && reconcileBlockers(s).length > 0 && (
            <Banner tone="warn" title="Before this day can be reconciled">
              <ul className="list-disc pl-4">
                {reconcileBlockers(s).map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </Banner>
          )}
        </div>
      )}
    </Section>
  )
}

/** Full spec only: the operator's rolls to take back or void (V1 rolls belong to the village). */
function OperatorRolls() {
  const { id } = useParams()
  const fmt = useFormat()
  const s = useSession(id).data!
  const rolls = useRolls({ issuedToId: s.operatorId ?? undefined, page: 1, limit: 50 })
  return (
    <Section title="Operator's rolls" eyebrow="Return or void" bodyClassName="p-0">
      {!s.operatorId ? (
        <p className="p-4 text-[14px] text-muted">No operator recorded.</p>
      ) : (
        <ul className="divide-y divide-rule/70">
          {(rolls.data?.rows ?? []).map((roll) => (
            <li key={roll.id} className="flex flex-col gap-2 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[13px]">
                  {roll.kind} · {roll.villageCode ?? '—'} {roll.startSequence ?? ''}–
                  {roll.endSequence ?? ''}
                </span>
                <span className="font-mono text-[12px] text-muted">
                  {fmt.count(roll.unused)} unused
                </span>
              </div>
              <RollActions roll={roll} sessionId={s.id} />
            </li>
          ))}
          {rolls.data && rolls.data.rows.length === 0 && (
            <li className="px-4 py-3 text-[14px] text-muted">No rolls issued to this operator.</li>
          )}
        </ul>
      )}
    </Section>
  )
}
